import { Component, OnInit, Input } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-angular';
import * as moment from 'moment';

import {
  FormFieldsGeneratorService,
  FormFields
} from './../../../shared/services/form-fields-generator.service';

import {
  NewTokenRequestModel,
  NewTokenRequestResult,
  RequestLoginToolService
} from './../../services/request-login-tool.service';

import { DialogStateService } from './../../../dialogs/services/dialog-state.service';
import {
  AB_DATE_MOMENT_FORMAT,
  AB_LOGIN_FORM_URL,
  AB_LOGIN_NAME_PATTERN,
  AB_LOGIN_USERNAME_PATTERN
} from './../../../shared/constants';

@Component({
  selector: 'ab-login',
  templateUrl: './request-login-tool.component.html',
  styleUrls: ['./request-login-tool.component.scss']
})
export class RequestLoginToolComponent implements OnInit {
  @Input() rendering: ComponentRendering;

  public submitButtonText: Field;
  public formBottomText: Field;
  public formFields: FormFields;

  public maxDateOfBirth: moment.Moment = moment().subtract(14, 'years');
  public minDateOfBirth: moment.Moment = moment().subtract(100, 'years');
  public noPhone = false;
  public selectToken = false;
  public apiLoading = false;
  public apiError = false;

  public requestLoginToolForm = new UntypedFormGroup({
    mobileDeviceType: new UntypedFormControl('', [Validators.required]),
    name: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(AB_LOGIN_NAME_PATTERN),
      Validators.maxLength(50)
    ]),
    username: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(AB_LOGIN_USERNAME_PATTERN),
      Validators.maxLength(20),
      Validators.minLength(8)
    ]),
    dateOfBirth: new UntypedFormControl('', [Validators.required]),
    reason: new UntypedFormControl('', [Validators.required]),
    pickupBranch: new UntypedFormControl('', [Validators.required])
  });

  get mobileDeviceTypeField() {
    return this.requestLoginToolForm.get('mobileDeviceType');
  }

  get nameField() {
    return this.requestLoginToolForm.get('name');
  }

  get usernameField() {
    return this.requestLoginToolForm.get('username');
  }

  get dateOfBirthField() {
    return this.requestLoginToolForm.get('dateOfBirth');
  }

  get reasonField() {
    return this.requestLoginToolForm.get('reason');
  }

  get pickupBranchField() {
    return this.requestLoginToolForm.get('pickupBranch');
  }

  constructor(
    protected router: Router,
    private formFieldsGeneratorService: FormFieldsGeneratorService,
    private requestLoginToolService: RequestLoginToolService,
    private dialogStateService: DialogStateService
  ) {}

  ngOnInit() {
    this.submitButtonText = this.rendering.fields.submitButtonText as Field;
    this.formBottomText = this.rendering.fields.FormBottomText as Field;
    this.formFields = this.formFieldsGeneratorService.generateFormFields(
      this.rendering
    ) as FormFields;

    this.mobileDeviceTypeField.valueChanges.subscribe(value => {
      this.reasonField.reset('');
      if (value === 'Android' || value === 'iOS') {
        this.selectToken = true;
        this.noPhone = false;
        this.pickupBranchField.reset('');
        this.reasonField.clearValidators();
        this.reasonField.updateValueAndValidity();
        this.pickupBranchField.clearValidators();
        this.pickupBranchField.updateValueAndValidity();
      } else if (value === 'hardToken' || value === 'replaceToken') {
        this.selectToken = true;
        this.noPhone = true;
        this.pickupBranchField.setValidators(Validators.required);
        this.pickupBranchField.updateValueAndValidity();

        if (value === 'replaceToken') {
          this.reasonField.setValidators(Validators.required);
        } else {
          this.reasonField.clearValidators();
        }

        this.reasonField.updateValueAndValidity();
      } else {
        this.selectToken = false;
      }
    });
  }

  cancel() {
    this.router.navigate([AB_LOGIN_FORM_URL]);
  }

  submitAction() {
    if (this.requestLoginToolForm.valid) {
      this.apiLoading = true;
      const formValue = this.requestLoginToolForm.value;
      const newTokenRequestModel = <NewTokenRequestModel>{
        dateOfBirth: formValue.dateOfBirth.format(AB_DATE_MOMENT_FORMAT),
        name: formValue.name,
        username: formValue.username,
        mobileDeviceType: 'None'
      };

      if (
        formValue.mobileDeviceType === 'hardToken' ||
        formValue.mobileDeviceType === 'replaceToken'
      ) {
        newTokenRequestModel.tokenType = '2';
        newTokenRequestModel.pickupBranch = formValue.pickupBranch;
        newTokenRequestModel.reason =
          formValue.mobileDeviceType === 'replaceToken'
            ? formValue.reason
            : 'New';
      } else {
        newTokenRequestModel.tokenType = '1';
        newTokenRequestModel.mobileDeviceType = formValue.mobileDeviceType;
      }

      this.requestLoginToolService.send(newTokenRequestModel).subscribe(
        response => {
          this.handleResponse(response);
        },
        error => {
          this.handleResponse(error);
        }
      );
    }
  }

  private handleResponse(response: NewTokenRequestResult) {
    this.apiLoading = false;

    if (!response || !response.callIsSuccess) {
      this.apiError = true;
      this.requestLoginToolForm.reset();
    } else {
      this.openSucces();
    }
  }

  private openSucces() {
    // Depending on which token type is selected a different popup will be opened
    if (
      this.mobileDeviceTypeField.value === 'Android' ||
      this.mobileDeviceTypeField.value === 'iOS'
    ) {
      this.dialogStateService.openDialog('appSuccess').subscribe(() => {
        this.reset();
        this.router.navigate([AB_LOGIN_FORM_URL]);
      });
    } else {
      this.dialogStateService.openDialog('tokenSuccess').subscribe(() => {
        this.reset();
        this.router.navigate([AB_LOGIN_FORM_URL]);
      });
    }
  }

  private reset() {
    this.requestLoginToolForm.reset();
  }
}
